import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'wouter';
import { v4 as uuidv4 } from 'uuid';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';

import FormComponent from '../../../../common/components/FormComponent';
import type { Field } from '../../../../common/types/form';
import { Client } from '../../../../common/types/client';
import transformer from '../../../../common/helpers/transformer';
import { moveClientToCounselingCenter } from '../moveClientToCounselingCenter';
import { AppDispatch } from '../../../../store';
import { addClient, loadClients } from '../../../clientsReducer';
import clientSelector from '../../../clientSelector';

type Props = {
  item: Client;
  update: (item: Client) => void;
  save?: (item: Client) => void;
};

const field: Field<Client> = {
  key: 'transferToCounselingCenter',
  label: 'Überleitung in Beratungsstelle',
  fieldTypes: 'checkbox',
};

function MoveClientToCounselingCenter({ item, update, save }: Props) {
  const [location] = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const clients = useSelector(clientSelector.getClients);

  const payloadType = 'bs_client';
  const hasCounselingCenterId = item.counselingCenterId !== undefined && item.counselingCenterId !== null;
  const bsClientExists =
    clients.find((client) => client.payload.counselingCenterId === item.counselingCenterId) !== undefined;

  React.useEffect(() => {
    dispatch(loadClients(payloadType));
  }, []);

  if (field.fieldTypes === 'headline' || field.fieldTypes === 'valueIndicator' || field.fieldTypes === 'link') {
    return <></>;
  }

  return (
    <Paper
      variant='outlined'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'center',
        margin: '24px 0',
        padding: '20px 24px',
        minWidth: '300px',
        backgroundColor: '#FAFAFA',
      }}
    >
      <Typography fontSize='1.2rem' mb={2}>
        Interne Weitervermittlung
      </Typography>

      <Grid container rowSpacing={2} columnSpacing={4}>
        <Grid item xs={6}>
          <FormComponent field={field} disabled value={item[field.key]} handleBlur={() => {}} handleChange={() => {}} />
        </Grid>
        <Grid item xs={6}>
          <Button
            disabled={status === 'loading' || (item[field.key] === true && bsClientExists)}
            variant='contained'
            onClick={async () => {
              const id = uuidv4();

              const client = moveClientToCounselingCenter(item, clients, location);
              const payload = await transformer.fromObjectToBase64WithEncryption(client);

              await dispatch(addClient({ id, payloadType, payload }));

              update({
                ...item,
                [field.key]: true,
                counselingCenterId: id,
              });

              if (save !== null && save !== undefined) {
                save({
                  ...item,
                  [field.key]: true,
                  counselingCenterId: id,
                });
              }
            }}
          >
            Übertragen der Klientin zur Fachberatungsstelle
          </Button>
        </Grid>
        {hasCounselingCenterId === true && (
          <Grid item xs={12}>
            <Link href={`/fachberatungsstelle/clients/${item.counselingCenterId}`} target='_blank'>
              Link zur Fachberatungsstelle
            </Link>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}

export default MoveClientToCounselingCenter;
